import { Injectable } from '@angular/core';
import { Observable, firstValueFrom, map } from 'rxjs';
import { Platform } from '@ionic/angular';
import { NotificationService } from './notification.service';
import { isElectron } from 'src/app/shared/utils/platform.utils';
import {
    Auth,
    AuthCredential,
    AuthErrorCodes,
    authState,
    createUserWithEmailAndPassword,
    EmailAuthProvider,
    FacebookAuthProvider,
    getAdditionalUserInfo,
    getAuth,
    GoogleAuthProvider,
    linkWithCredential,
    linkWithPopup,
    multiFactor,
    OAuthProvider,
    reauthenticateWithCredential,
    reauthenticateWithPopup,
    sendEmailVerification,
    sendPasswordResetEmail,
    signInAnonymously,
    signInWithCredential,
    signInWithEmailAndPassword,
    signInWithPopup,
    User,
    UserCredential,
    PhoneAuthCredential,
    PhoneAuthProvider,
    PhoneInfoOptions,
    PhoneMultiFactorGenerator,
    getMultiFactorResolver,
    signInWithCustomToken,
} from '@angular/fire/auth';
import {
    SignInWithApple,
    SignInWithAppleResponse,
} from '@capacitor-community/apple-sign-in';
import { AuthStoreDispatcher } from '../state/auth/auth.dispatcher';
import { DynamicContentService } from 'src/app/shared/services/dynamic-content/dynamic-content.service';
import { SopoLoadingService } from 'src/app/shared/services/sopo-loading/sopo-loading.service';
import { DEFAULT_DISMISS_AFTER_TIME, SopoPopupService } from 'src/app/sopo-popup/service/sopo-popup.service';
import { SopoConfirmSettings } from 'src/app/sopo-popup/interfaces/confirm';
import { SopoStringInputSettings } from 'src/app/sopo-popup/interfaces/string-input';
import { SopoAlertSettings } from 'src/app/sopo-popup/interfaces/alert';
import { environment } from 'src/environments/environment';
import { IOnUserCreated } from '@soposopo/sopo-models';
import { ApiService } from '../backend/api.service';
import { ElectronService } from 'src/app/shared/services/electron/electron.service';
import { SopoDialogRef } from 'src/app/sopo-popup/interfaces/dialog';
import { ProfileStateDispatcher } from '../state/profile/profile.dispatcher';
import { SopoPhoneNumberInputSettings } from 'src/app/sopo-popup/interfaces/phone-number-input';
import { SupportedWallets } from '../state/auth/auth.state';
import { WalletHandlerService } from '../wallets/walletHandler.service';
import { ProfileService } from './profile.service';
import { ThemeService } from './theme.service';
import { TwitterPixelService } from 'src/app/shared/services/twitter-pixel/twitter-pixel.service';
/*
  Generated class for the FirebaseProvider provider.

  See https://angular.io/guide/dependency-injection for more info on providers
  and Angular DI.
*/

export const REAUTH_REQUIRED_ERROR_CODES: string[] = [
    'auth/requires-recent-login',
    'auth/user-token-expired',
    'auth/user-not-found',
    'auth/invalid-user-token',
    'auth/invalid-id-token',
    'auth/id-token-expired',
    'auth/token-expired',
    'auth/credential-too-old',
    'auth/operation-not-allowed',
    'auth/no-current-user'
];

@Injectable({ providedIn: "root" })
export class AuthService {
    user: Observable<User | null>;
    data: any;

    private _phoneNumberDialogRef: SopoDialogRef | null = null;

    get phoneNumberDialogRef(): SopoDialogRef {
        return this._phoneNumberDialogRef;
    }

    constructor(
        private firebaseAuth: Auth,
        private platform: Platform,
        private notificationService: NotificationService,
        private authStoreDispatcher: AuthStoreDispatcher,
        private dynamicContentService: DynamicContentService,
        private loadingService: SopoLoadingService,
        private sopoPopupService: SopoPopupService,
        private apiService: ApiService,
        private electronService: ElectronService,
        private profileDispacher: ProfileStateDispatcher,
        private walletHandler: WalletHandlerService,
        private profileService: ProfileService,
        private themeService: ThemeService,
        private twitterPixelService: TwitterPixelService,
    ) {
        this.user = authState(firebaseAuth);
    }

    private async checkTermsAccepted(userId: string): Promise<void> {
        const termsAcceptedInProfileData = await firstValueFrom(this.profileDispacher.getProfileState().pipe(
            map(state => state.profileData.termsAccepted),
        ));

        if (termsAcceptedInProfileData) return;

        const termsAcceptedOnRegistration = await firstValueFrom(this.profileDispacher.getProfileState().pipe(
            map(state => state.termsAcceptedOnRegistration),
        ));

        if (termsAcceptedOnRegistration && userId) {
            await this.profileService.setUserTermsToAccepted(userId);
        }
    }

    async createUserWithEmailAndPassword(email: string, password: string, inviteCode: string | null): Promise<UserCredential> {
        try {
            await this.authStoreDispatcher.logout();
            const userCredentials = await createUserWithEmailAndPassword(this.firebaseAuth, email, password);
            await this.notifyBEOnUserCreated(userCredentials.user.uid, inviteCode);
            this.twitterPixelService.signUp();
            return userCredentials;
        } catch (error) {
            // Handle Errors here.
            let errorMessage = '';
            switch (error.code) {
                case AuthErrorCodes.EMAIL_EXISTS: {
                    errorMessage = 'Email already in use.';
                    break;
                }
                case AuthErrorCodes.INVALID_EMAIL: {
                    errorMessage = 'Invalid email.'
                    break;
                }
                case AuthErrorCodes.WEAK_PASSWORD: {
                    errorMessage = 'Password should be at least 6 characters.';
                    break;
                }
                case AuthErrorCodes.NETWORK_REQUEST_FAILED: {
                    errorMessage = 'Please check your internet connection and try again later.';
                    break;
                }
                default: {
                    errorMessage = 'Unexpected error occured.';
                    break;
                }
            }
            this.sopoPopupService.showAlert('app', {
                header: 'Registration Error',
                message: errorMessage,
                dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
            });
            throw error;
        }
    }

    async signInWithEmailAndPassword(email: string, password: string): Promise<UserCredential> {
        try {
            const userCredential = await signInWithEmailAndPassword(this.firebaseAuth, email, password)
            return userCredential;
        } catch (error) {
            if (error.code == 'auth/multi-factor-auth-required') {
                try {
                    return await this.solveTwoFactorAuthentication(error);
                } catch (error) {
                    throw error;
                }
            }
            await this.authErrorHandler(error);
            throw error;
        }
    }

    async signInAsGuest(): Promise<UserCredential> {
        await this.authStoreDispatcher.logout();
        try {
            const user = await signInAnonymously(this.firebaseAuth);
            localStorage.setItem('anonymusUserId',user.user.uid)
            return user;
        } catch (error) {
            this.sopoPopupService.showAlert('app', {
                header: 'Error',
                message: 'Failed to sign in as a Guest.',
                dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
            });
            throw error;
        }
    }

    async signInWithFacebook(inviteCode: string | null): Promise<User | UserCredential> {
        try {
            await this.authStoreDispatcher.logout();
            let userCredential: UserCredential;
            if (this.electronService.isElectronApp()) {
                const credentialToken = await this.electronService.externalLogin("facebook");
                const authCredential = FacebookAuthProvider.credential(credentialToken);
                userCredential = await signInWithCredential(this.firebaseAuth, authCredential);
            } else {
                const authProvider = (new FacebookAuthProvider());
                userCredential = await signInWithPopup(this.firebaseAuth, authProvider);
            }
            const isRegistration = getAdditionalUserInfo(userCredential).isNewUser;
            this.checkTermsAccepted(userCredential.user.uid);
            if (isRegistration) {
                await this.notifyBEOnUserCreated(userCredential.user.uid, inviteCode);
                this.twitterPixelService.signUp();
            }
            return userCredential;
        } catch (error) {
            if (error.code === 'auth/multi-factor-auth-required'){
                try {
                    return await this.solveTwoFactorAuthentication(error);
                } catch (error) {
                    throw error;
                }
            }
            await this.authErrorHandler(error);
            throw error;
        }
    }

    async signInWithGoogle(inviteCode: string | null): Promise<User | UserCredential> {
        try {
            await this.authStoreDispatcher.logout();
            let userCredential: UserCredential;
            if (this.electronService.isElectronApp()) {
                const credentialToken = await this.electronService.externalLogin("google");
                const authCredential = GoogleAuthProvider.credential(credentialToken);
                userCredential = await signInWithCredential(this.firebaseAuth, authCredential);
            } else {
                const provider = new GoogleAuthProvider();
                userCredential =  await signInWithPopup(this.firebaseAuth, provider);
            }
            const isRegistration = getAdditionalUserInfo(userCredential).isNewUser;
            this.checkTermsAccepted(userCredential.user.uid);
            if (isRegistration) {
                await this.notifyBEOnUserCreated(userCredential.user.uid, inviteCode);
                this.twitterPixelService.signUp();
            }
            return userCredential;
        } catch (error) {
            if (error.code === 'auth/multi-factor-auth-required'){
                try {
                    return await this.solveTwoFactorAuthentication(error);
                } catch (error) {
                    throw error;
                }
            }
            await this.authErrorHandler(error);
            throw error;
        }
    }

    async signInWithApple(inviteCode: string | null): Promise<UserCredential> {
        try {
            await this.authStoreDispatcher.logout();
            let userCredential: UserCredential;
            if (this.electronService.isElectronApp()) {
                const credentialToken = await this.electronService.externalLogin("apple");
                const authCredential = new OAuthProvider('apple.com').credential({ idToken: credentialToken});
                userCredential = await signInWithCredential(this.firebaseAuth, authCredential)
            } else {
                const provider = new OAuthProvider('apple.com');
                provider.addScope('email');
                provider.addScope('name');
                userCredential = await signInWithPopup(this.firebaseAuth, provider);
            }
            const isRegistration = getAdditionalUserInfo(userCredential).isNewUser;
            this.checkTermsAccepted(userCredential.user.uid);
            if (isRegistration) {
                await this.notifyBEOnUserCreated(userCredential.user.uid, inviteCode);
                this.twitterPixelService.signUp();
            }
            return userCredential;
        } catch (error) {
            if (error.code === 'auth/multi-factor-auth-required'){
                try {
                    return await this.solveTwoFactorAuthentication(error);
                } catch (error) {
                    throw error;
                }
            }
            await this.authErrorHandler(error);
            throw error;
        }
    }

    async signInWithWallet(inviteCode: string | null, wallet: SupportedWallets) {
        try {
            const customToken = await this.walletHandler.signInWithWallet(wallet);
            const userCredential = await signInWithCustomToken(this.firebaseAuth, customToken);
            const startTime = await this.apiService.getServerTime() * 1000 - 20000;
            const isRegistration = new Date(userCredential.user.metadata.creationTime).getTime() > startTime;
            this.authStoreDispatcher.setIsRegistration(isRegistration);
            this.checkTermsAccepted(userCredential.user.uid);
            if (isRegistration) {
                const state = await firstValueFrom(this.profileDispacher.getProfileState())
                const autoJoinClubNedeed = wallet === 'abstract';
                await this.notifyBEOnUserCreated(userCredential.user.uid, inviteCode, autoJoinClubNedeed);
                this.twitterPixelService.signUp();
            }
            return userCredential;
        } catch (error) {
            console.error(error)
            this.authStoreDispatcher.setIsRegistration(null);
            if (error.code === 'auth/multi-factor-auth-required'){
                try {
                    return await this.solveTwoFactorAuthentication(error);
                } catch (error) {
                    throw error;
                }
            }
            const alertOptions: SopoAlertSettings = {
                header: 'Abstract Wallet Error',
                subHeader: error.message ?? `Something went wrong, \nPlease try again with another ${wallet} account.`,
                dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
            }
            await this.sopoPopupService.showAlert('app', alertOptions);
            throw error;
        }
    }

    async confirmWithApple() {
        await this.authStoreDispatcher.logout();
        const appleCredential: SignInWithAppleResponse = await SignInWithApple.authorize();
        return appleCredential.response.authorizationCode;
    }

    getUserProviderId(): string | null {
        const user = this.firebaseAuth.currentUser;
        if (!user || user.providerData.length === 0) return null;
        return user.providerData[0].providerId;
    }

    async solveTwoFactorAuthentication(error){
        try {
            const auth = getAuth();
            const resolver = getMultiFactorResolver(auth, error);
            if (resolver.hints[0].factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
                const phoneInfoOptions = {
                    multiFactorHint: resolver.hints[0],
                    session: resolver.session
                };
                const credential = await this.verifyPhoneNumber(phoneInfoOptions);
                const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(credential);
                const userCredential = await resolver.resolveSignIn(multiFactorAssertion);
                this.loadingService.dismiss('auth');
                return userCredential;
            }
        } catch (error) {
            if (!(await firstValueFrom(authState(this.firebaseAuth)))) this.authErrorHandler(error);
            throw error;
        }
    }

    async enrollTwoFactorAuthentication(fromVerificationGuard?: boolean): Promise<boolean>{
        try {
            this.loadingService.dismiss('auth');
            this.sopoPopupService.clearViewContainer('app');
            if (!fromVerificationGuard) {
                const answerOption: SopoConfirmSettings = {
                    data : {
                        header: 'Activate Two-Factor-Authentication',
                        body: 'Do you want to continue?',
                        okText: 'Yes',
                        cancelText: 'No',
                    }
                }
                const confirm = await this.sopoPopupService.promptForConfirmation('app', answerOption);
                if (!confirm) return false;
            }

            this.loadingService.present('auth');
            let multiFactorSession = await multiFactor(getAuth().currentUser).getSession();
            this.loadingService.dismiss('auth');

            const options: SopoPhoneNumberInputSettings ={
                data: {
                    header: 'Two Factor Authentication',
                    body: 'Please enter your phone number',
                    okText: 'Confirm',
                    cancelText: 'Cancel',
                    isInUS: fromVerificationGuard,
                }
            }
            this.loadingService.dismiss('auth');
            const phoneNumber = await this.sopoPopupService.promptForPhoneNumber('app', options);
            if (!phoneNumber) {
                return false;
            }

            this.loadingService.present('auth');
            let credential: PhoneAuthCredential;
            let phoneInfoOptions = {
                phoneNumber: phoneNumber,
                session: multiFactorSession
            };
            try {
                credential = await this.verifyPhoneNumber(phoneInfoOptions);
            } catch (error) {
                if (error.code && REAUTH_REQUIRED_ERROR_CODES.includes(error.code)) {
                    this.loadingService.dismiss('auth');
                    await this.verifyProvider();
                    multiFactorSession = await multiFactor(getAuth().currentUser).getSession();
                    phoneInfoOptions.session = multiFactorSession;
                    credential = await this.verifyPhoneNumber(phoneInfoOptions);
                }
                else throw error;
            }

            const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(credential);
            await multiFactor(getAuth().currentUser).enroll(multiFactorAssertion);

            try {
                await this.twoFactorAuthenticationSavePhoneNumber(phoneNumber);
            } catch (error) {
                console.error('[AuthService] - Could not save 2fa phone number!');
            }

            await this.showAlert('Success', 'You have successfully added Two-Factor-Authentication to your account.');
            this.loadingService.dismiss('auth');
            return true;
        } catch (error) {
            await this.authErrorHandler(error);
            return false;
        }
    }

    async unenrollTwoFactorAuthentication(): Promise<boolean>{
        try{
            const options: SopoConfirmSettings = {
                data: {
                    header: 'Deactivate Two-Factor-Authentication',
                    body: 'Do you want to continue?',
                    okText: 'Yes',
                    cancelText: 'No',
                }
            }
            const answer = await this.sopoPopupService.promptForConfirmation('app', options);
            if (!answer) return false;
            await this.verifyProvider();
            const multiFactorUser = multiFactor(getAuth().currentUser);
            const factors = multiFactorUser.enrolledFactors;
            await multiFactorUser.unenroll(factors[0]);
            this.loadingService.dismiss('auth');
            const alertOptions: SopoAlertSettings = {
                header: 'Success',
                message: 'You have removed Two-Factor-Authentication from your account.',
                dismissAfter: 5000,
            }
            this.sopoPopupService.showAlert('app', alertOptions);
            return true;
        } catch (error) {
            await this.authErrorHandler(error);
            return false;
        }
    }

    async verifyProvider(){
        const providerId = this.getUserProviderId();
        let credential: UserCredential;
        try{
            switch(providerId){
                case 'password':
                    const passwordOptions: SopoStringInputSettings = {
                        data: {
                            header: 'Two-Factor-Authentication',
                            body: 'Please enter your password.',
                            inputType: 'password',
                            okText: 'Confirm',
                            cancelText: 'Cancel',
                        }
                    }
                    const password = await this.sopoPopupService.promptForString('app', passwordOptions);
                    if (!password) throw new Error('Missing Password');
                    credential = await this.reauthenticateWithPassword(password);
                break;
                case 'google.com':
                    credential = await this.reauthenticateWithGoogleOrFacebook('google.com');
                break;
                case 'facebook.com':
                    credential = await this.reauthenticateWithGoogleOrFacebook('facebook.com');
                break;
                case 'apple.com':
                    credential = await this.reauthenticateWithApple();
                break;
            }
            return credential;
        } catch(error) {
            if (error.message === 'Missing Password') error.code = 'missing-password';
            throw error;
        }
    }

    async verifyPhoneNumber(phoneInfoOptions: PhoneInfoOptions){
        try{
            console.log('befor captcha init')
            const recaptchaVerifier = this.dynamicContentService.initReCaptcha();
            console.log(' captcha init done')
            const phoneAuthProvider = new PhoneAuthProvider(getAuth());

            console.log('start verify phone number')
            const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);
            console.log('verify phone number done')
            let title = 'Please Enter The Verification Code Sent To The Provided Phone Number.';
            if ('phoneNumber' in phoneInfoOptions) {
                const lastNumbersOfPhoneNumber = phoneInfoOptions.phoneNumber.slice(-2);
                title = `Please Enter The Verification Code Sent To The Phone Number Ending In **${lastNumbersOfPhoneNumber}.`;
            }
            this.loadingService.dismiss('auth');
            let verificationCode: string;
            if (this.sopoPopupService.checkForViewContainerRefs('app')) {
                const options: SopoStringInputSettings = {
                    data: {
                        header: title,
                        okText: 'Confirm',
                        numberOnly: true,
                        inputType: 'tel',
                    }
                };
                verificationCode = await this.sopoPopupService.promptForString('app', options);
            } else {
                const options: SopoStringInputSettings = {
                    data: {
                        header: title,
                        okText: 'Confirm',
                    }
                };
                verificationCode = await this.sopoPopupService.promptForString('app', options);
            }
            this.loadingService.present('auth');
            return PhoneAuthProvider.credential(verificationId, verificationCode);
        } catch (error) {
            console.log(error);
            throw error;
        }
    }

    async reauthenticateWithPassword(password: string) {
        try {
            this.loadingService.present('auth');
            const credential: AuthCredential = EmailAuthProvider.credential(getAuth().currentUser.email, password);
            return await reauthenticateWithCredential(getAuth().currentUser, credential);
        } catch (error) {
            if (error.code === 'auth/multi-factor-auth-required'){
                try {
                    return await this.solveTwoFactorAuthentication(error);
                } catch (error) {
                    throw error;
                }
            }
            throw error;
        }
    }

    async reauthenticateWithGoogleOrFacebook(provider: string){
        try {
            let socialCredential;
            let socialProvider;
            this.loadingService.present('auth');
            if (provider === 'google.com') {
                if (isElectron()) {
                    const credentialToken = await this.electronService.externalLogin("google");
                    socialCredential = GoogleAuthProvider.credential(credentialToken);
                } else {
                    socialProvider = new GoogleAuthProvider();
                }
            } else {
                if (isElectron()) {
                    const credentialToken = await this.electronService.externalLogin("facebook");
                    socialCredential = FacebookAuthProvider.credential(credentialToken);
                } else {
                    socialProvider = new FacebookAuthProvider();
                }
            }
            if(!isElectron()){
                return await reauthenticateWithPopup(getAuth().currentUser, socialProvider);
            } else {
                return await reauthenticateWithCredential(getAuth().currentUser, socialCredential);
            }
        } catch (error) {
            if (error.code === 'auth/multi-factor-auth-required'){
                try {
                    return await this.solveTwoFactorAuthentication(error);
                } catch (error) {
                    throw error;
                }
            }
            throw error;
        }
    }


    async reauthenticateWithApple(){
        try {
            this.loadingService.present('auth');
            if (this.electronService.isElectronApp()) {
                const credentialToken = await this.electronService.externalLogin("apple");
                const authCredential = new OAuthProvider('apple.com').credential({ idToken: credentialToken});
                return reauthenticateWithCredential( getAuth().currentUser, authCredential);
            } else {
                const provider = new OAuthProvider('apple.com');
                provider.addScope('email');
                provider.addScope('name');
                return await reauthenticateWithPopup(getAuth().currentUser, provider);
            }
        } catch (error) {
            if (error.code === 'auth/multi-factor-auth-required'){
                try {
                    return await this.solveTwoFactorAuthentication(error);
                } catch (error) {
                    throw error;
                }
            }
            throw error;
        }
    }

    async linkWithEmail(email: string, password: string, inviteCode: string | null): Promise<boolean> {
        try {
            const credential = EmailAuthProvider.credential(email, password);
            const userCredentials = await linkWithCredential(this.firebaseAuth.currentUser, credential);
            await this.notifyBEOnUserCreated(userCredentials.user.uid, inviteCode);
            this.twitterPixelService.signUp();
            await signInWithEmailAndPassword(this.firebaseAuth, email, password);
            this.authStoreDispatcher.nonAnonymousLogin();
            return true;
        } catch (error) {
            throw error;
        }
    }

    async linkWithGoogle(inviteCode: string | null) {
        try {
            const provider = new GoogleAuthProvider();
            const userCredentials = await linkWithPopup(this.firebaseAuth.currentUser, provider);
            await this.notifyBEOnUserCreated(userCredentials.user.uid, inviteCode);
            this.twitterPixelService.signUp();
            this.authStoreDispatcher.nonAnonymousLogin();
            return true;
        } catch (error) {
            throw error;
        }
    }

    async linkWithFacebook(inviteCode: string | null) {
        try {
            const provider = new FacebookAuthProvider();
            const userCredentials = await linkWithPopup(this.firebaseAuth.currentUser, provider);
            await this.notifyBEOnUserCreated(userCredentials.user.uid, inviteCode);
            this.twitterPixelService.signUp();
            this.authStoreDispatcher.nonAnonymousLogin();
            return true;
        } catch (error) {
            throw error;
        }
    }

    async linkWithApple(inviteCode: string | null) {
        try {
            const provider = new OAuthProvider('apple.com');
            provider.addScope('email');
            provider.addScope('name');
            const userCredentials = await linkWithPopup(this.firebaseAuth.currentUser, provider);
            await this.notifyBEOnUserCreated(userCredentials.user.uid, inviteCode);
            this.twitterPixelService.signUp();
            this.authStoreDispatcher.nonAnonymousLogin();
            return true;
        } catch (error) {
            throw error;
        }
    }

    async linkWithWallet(wallet: SupportedWallets ,inviteCode: string | null): Promise<boolean> {
        try {
            const userId = this.firebaseAuth.currentUser.uid;
            const customToken = await this.walletHandler.signInWithWallet(wallet, userId);
            const autoJoinClubNedeed = wallet === 'abstract';
            const userCredentials = await signInWithCustomToken(this.firebaseAuth, customToken);
            if (this.firebaseAuth.currentUser.providerData.length === 0) {
                this.authStoreDispatcher.setIsRegistration(true);
                await this.notifyBEOnUserCreated(userCredentials.user.uid, inviteCode, autoJoinClubNedeed);
                this.twitterPixelService.signUp();
            }
            this.authStoreDispatcher.nonAnonymousLogin();
            return true;
        } catch (error) {
            this.authStoreDispatcher.setIsRegistration(null);
            throw error;
        }
    }

    async sendEmailVerification(){
        const providerId = this.getUserProviderId();
        this.loadingService.present('auth');
        sendEmailVerification(getAuth().currentUser).then(() => {
            this.loadingService.dismiss('auth');
            this.showAlert('Verification email sent', 'Verification link has been sent to your email');
        })
        .catch((error) => {
            this.loadingService.dismiss('auth');
            if (error.code && error.code === "auth/too-many-requests") this.showAlert('Verification email error', 'Too many attempts, please try again later.');
            else this.showAlert('Verification email error', 'Please try again.');
        });
    }

    async sendPasswordResetEmail(email: string): Promise<any> {
        await sendPasswordResetEmail(this.firebaseAuth, email)
            .then(() => {
                this.data = {
                    header: `Reset Link Sent`,
                    message: 'Reset link has been sent to ' + email,
                };
            })
            .catch(() => {
                this.data = {
                    header: `Reset Password Error`,
                    message: 'Email not found. Please check your email or sign up to create an account.',
                };
            });
        this.sopoPopupService.showAlert('app', {
            header: this.data.header,
            message: this.data.message,
            dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
        });
    }

    async signOut(isUserDeleting: boolean, logInOnOtherDevice?: boolean): Promise<void> {
        if (this.firebaseAuth.currentUser) {
            const currentUser = this.firebaseAuth.currentUser;
            const userID = currentUser!.uid;
            if (!isUserDeleting && !logInOnOtherDevice) await this.notificationService.updateFcmToken('', userID);
        }
        return this.firebaseAuth.signOut();
    }

    async notifyBEOnUserCreated(userId: string, inviteCode: string | null, autoJoinClubNedeed = false): Promise<void> {
        let payload: IOnUserCreated;
        if (inviteCode) {
            payload = {
                userId,
                inviteCode,
                autoJoinClubNedeed
            }
        } else {
            payload = {
                userId,
                autoJoinClubNedeed
            }
        }
        await this.apiService.notifyBEOnUserCreated(payload);
    }

    async authErrorHandler(error){
        if (!error.code) {
            if (error.message === 'Location services are not enabled') {
                await this.showAlert('Location Services Disabled', 'Please enable location services to continue.');
            }
            else if (error.message === 'Could not save phone number!') {
                await this.showAlert('Verification Error', 'Could not save phone number, please try again later.');
            }
            else if (error.message === 'Phone number already taken!') {
                await this.showAlert('Verification Error', 'Phone number already in use.');
            }
            else if (error.message === 'Verified call failed!') {
                await this.showAlert('Verification Error', 'Verified.inc phone number verification failed.');
            }
            else {
                console.error('[AuthErrorHandler] - Error code is undefined!', error);
                const message = (environment.staging && error.message) ? 'Please try again.' + '<br/>' + `(${error.message})` : 'Please try again.';
                await this.showAlert('An Error Occured', message);
            }
        } else {
            switch (error.code) {
                case 'auth/invalid-phone-number':
                    await this.showAlert('Verification Error', 'Invalid phone number.');
                    break;
                case 'auth/unverified-email':
                    this.loadingService.dismiss('auth');
                    const resend = await this.sopoPopupService.promptForConfirmation('app', {
                        data: {
                            header: 'Verification Error',
                            body: 'You must verify your email address. A verification email has been sent to your address. If you didn’t receive it, you can resend it now.',
                            cancelText: 'Dismiss',
                            okText: 'Resend Email'
                        }
                    });
                    if (resend) {
                        await this.sendEmailVerification();
                    }
                    break;
                case 'auth/wrong-password':
                case 'auth/user-not-found':
                    await this.showAlert('Authentication Error', 'You have entered the wrong credentials.');
                    break;
                case 'auth/invalid-verification-code':
                    await this.showAlert('Verification Error', 'Wrong Verification Code.');
                    break;
                case 'auth/invalid-action-code':
                    await this.showAlert('Password Reset Error', 'Your action code is invalid, please try to resend an email.');
                    break;
                case 'auth/expired-action-code':
                    await this.showAlert('Password Reset Error', 'Your action code is expired, please try to resend an email.');
                    break;
                case 'auth/too-many-requests':
                    await this.showAlert('Authentication Error', 'Too many attempts in a short period of time, please try again later.');
                    break;
                case 'auth/missing-code':
                    this.showAlert('Verification Error', 'Verification Code is missing.');
                    break;
                case 'auth/popup-closed-by-user':
                    this.showAlert('Authentication', 'The popup has been closed by the user before finalizing the authentication.');
                    break;
                case 'auth/internal-error-encountered.':
                    await this.showAlert('Authentication Error', 'Unable to verify phone number, please try again later.');
                    break;
                case 'missing-password':
                    await this.showAlert('Authentication Error', 'Password is missing.');
                    break;
                case 'auth/user-disabled':
                    await this.showAlert('Account suspended', 'Your account has been suspended, please email ' +
                        '<a href="mailto:support@playsopo.com?subject=Account%20suspended">support@playsopo.com</a> ' +
                        'with your user name or short ID for more information.',
                        60000, 
                        this.themeService.themePath === 'black-gold-theme' ? ['sopo-popoup-yellow-link'] : ['sopo-popoup-light-blue-link']
                    );
                    break;
                case 'auth/network-request-failed':
                    await this.showAlert('Network Error', 'Please check your internet connection, and try again later.');
                    break;
                default:
                    console.error(error);
                    const message = environment.staging ? 'Please try again.' + '<br/>' + `(${error.code})` : 'Please try again.';
                    this.showAlert('An Error Occured', message );
                    break;
            }
        }
        this.loadingService.dismiss('auth');
        this.dynamicContentService.closeReCaptcha();
        if (localStorage.getItem('hasWeakPassword') === 'true') localStorage.removeItem('hasWeakPassword');
    }

    private async showAlert(header: string, message: string, dismissAfter = DEFAULT_DISMISS_AFTER_TIME, sCss = []) {
        this.sopoPopupService.clearViewContainer('app');
        const sopoAlertSettings: SopoAlertSettings = {
            header,
            message,
            dismissAfter,
            sCss,
        };
        await this.sopoPopupService.showAlert('app', sopoAlertSettings);
    }

    public async getPhoneNumber(): Promise<string> {
        const options: SopoPhoneNumberInputSettings = {
            data: {
                header: 'Please enter your phone number',
                okText: 'Confirm',
                cancelText: 'Cancel',
                checkNumber: true,
            },
            zIndex: 2001,
        }
        const dialogRef = await this.sopoPopupService.promptForPhoneNumber('app', options, true) as SopoDialogRef;
        this._phoneNumberDialogRef = dialogRef;

        return firstValueFrom(dialogRef.afterClosed);
    }

    public resetPhoneNumberDialogRef(): void {
        this._phoneNumberDialogRef = null;
    }

    private async twoFactorAuthenticationSavePhoneNumber(phoneNumber: string): Promise<void> {
        const profileState = await firstValueFrom(this.profileDispacher.getProfileState());
        await this.apiService.savePhoneNumber(profileState.profileData.uid, { phoneNumber });
    }

    async promptForConfirmation(header: string, message: string): Promise<boolean> {
        this.sopoPopupService.clearViewContainer('app');
        const sopoAlertSettings: SopoConfirmSettings = {
            data: {
                header: header,
                body: message,
                okText: 'Verify'
            }
        };
        return await this.sopoPopupService.promptForConfirmation('app', sopoAlertSettings);
    }
}
